<template>
  <div class="egresos-container">
    <!-- Tabla de Órdenes de Egreso -->
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center flex-wrap">
        <span class="egresos-title">Gestión de Egresos</span>
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="showNewEgresoModal"
          size="default"
        >
          <span class="d-none d-sm-inline">Nueva Salida</span>
          <span class="d-sm-none">Nueva</span>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Filtros -->
        <v-row class="mb-4 egresos-filters">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.dateFrom"
              label="Fecha desde"
              type="date"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.dateTo"
              label="Fecha hasta"
              type="date"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar"
              placeholder="Orden, cliente, RUT..."
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Tabla -->
        <ResponsiveDataTable
          :items="filteredEgresos"
          :headers="[
            { title: 'Número de Orden', key: 'order_number', align: 'start', sortable: true },
            { title: 'Fecha', key: 'created_at', align: 'start', sortable: true },
            { title: 'Área/Servicio', key: 'area_servicio', align: 'start', sortable: true },
            { title: 'Estrategia', key: 'strategy', align: 'center', sortable: true },
            { title: 'SKUs', key: 'items_count', align: 'end', sortable: true },
            { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
          ]"
          :loading="loading"
          :search="filters.search"
          class="elevation-1 egresos-table"
          :show-select="false"
          items-per-page="10"
          density="default"
          hover
        >
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.area_servicio="{ item }">
            {{ getAreaServicioText(item.area_servicio) }}
          </template>

          <template v-slot:item.strategy="{ item }">
            <v-chip :color="getStrategyColor(item.strategy)" size="small">
              {{ getStrategyText(item.strategy) }}
            </v-chip>
          </template>

          <template v-slot:item.items_count="{ item }">
            {{ item.items_count || 0 }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon="mdi-eye"
              size="small"
              @click="viewEgreso(item)"
              color="primary"
              variant="text"
              class="mr-1"
            ></v-btn>
            <v-btn
              icon="mdi-delete"
              size="small"
              @click="deleteEgreso(item)"
              color="error"
              variant="text"
            ></v-btn>
          </template>
        </ResponsiveDataTable>
      </v-card-text>
    </v-card>

    <!-- Modal Nueva Salida -->
    <v-dialog v-model="showCreateModal" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEditingEgreso ? 'Editar Salida' : 'Nueva Salida' }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="egresoForm" v-model="formValid">
            <!-- Área/Servicio y Estrategia -->
            <v-row>
              <v-col cols="12" md="8">
                <v-select
                  v-model="newEgreso.area_servicio"
                  :items="areasServicios"
                  label="Área o Servicio *"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'Área o Servicio es requerido']"
                  prepend-inner-icon="mdi-hospital-building"
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="newEgreso.strategy"
                  :items="strategyOptions"
                  label="Estrategia de Picking"
                  variant="outlined"
                  density="compact"
                  @update:model-value="onStrategyChanged"
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Información adicional -->
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="newEgreso.notes"
                  label="Notas adicionales"
                  rows="2"
                  variant="outlined"
                  density="compact"
                ></v-textarea>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Productos -->
            <div class="d-flex justify-space-between align-center mb-4">
              <h3>Productos</h3>
              <div class="d-flex gap-2">
                <v-btn
                  color="secondary"
                  prepend-icon="mdi-barcode"
                  @click="startSingleScan"
                  size="small"
                  variant="outlined"
                  :disabled="loadingInitialData"
                >
                  Escanear Productos
                </v-btn>
                <!-- Botones comentados - no se utilizan
                <v-btn
                  color="info"
                  prepend-icon="mdi-barcode-scan"
                  @click="startMultipleScan"
                  size="small"
                  variant="outlined"
                  :disabled="loadingInitialData"
                >
                  Escaneo Masivo
                </v-btn>
                <v-btn
                  color="primary"
                  prepend-icon="mdi-plus"
                  @click="addProductManually"
                  size="small"
                  :disabled="loadingInitialData"
                >
                  Agregar Manual
                </v-btn>
                -->
              </div>
            </div>

            <!-- Tabla de productos -->
            <v-card variant="outlined" class="mb-4" v-if="newEgreso.items.length > 0">
              <v-card-text class="pa-2">
                <v-table class="egresos-items-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="product-col">Producto</th>
                        <th class="sku-col">SKU</th>
                        <th class="quantity-col">Cantidad</th>
                        <th class="lot-col">Lote</th>
                        <th class="expiry-col">Vencimiento</th>
                        <th class="serial-col">S/N</th>
                        <th class="production-col">F. Fabricación</th>
                        <th class="pack-col">F. Empaque</th>
                        <th class="sscc-col">SSCC</th>
                        <th class="gln-col">GLN</th>
                        <th class="strategy-col">Estrategia</th>
                        <th class="actions-col">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in newEgreso.items" :key="index">
                        <td class="product-col">
                          <div>{{ item.product_name || 'Producto no encontrado' }}</div>
                          <!-- Mostrar pesos y campos personalizados si existen -->
                          <div v-if="hasWeightOrCustomData(item)" class="text-caption text-grey-600 mt-1">
                            <span v-if="item.gross_weight_kg">Peso: {{ item.gross_weight_kg }}kg</span>
                            <span v-if="item.gross_weight_lb && !item.gross_weight_kg">Peso: {{ item.gross_weight_lb }}lb</span>
                            <span v-if="item.gdti" class="ms-2">GDTI: {{ item.gdti }}</span>
                            <span v-if="hasCustomFields(item)" class="ms-2">
                              <v-icon size="x-small" color="info">mdi-tag</v-icon>
                              {{ getCustomFieldsCount(item) }} campos
                            </span>
                          </div>
                        </td>
                        <td class="sku-col">{{ item.product_sku || '-' }}</td>
                        <td class="quantity-col">{{ item.quantity }}</td>
                        <td class="lot-col">{{ item.lot_number || '-' }}</td>
                        <td class="expiry-col">{{ formatDate(item.expiry_date) || '-' }}</td>
                        <td class="serial-col">{{ item.serial_number || '-' }}</td>
                        <td class="production-col">{{ formatDate(item.production_date) || '-' }}</td>
                        <td class="pack-col">{{ formatDate(item.pack_date) || '-' }}</td>
                        <td class="sscc-col">{{ item.sscc || '-' }}</td>
                        <td class="gln-col">{{ item.gln || '-' }}</td>
                        <td class="strategy-col">
                          <v-chip 
                            v-if="!item.strategy_compliant"
                            color="warning"
                            size="small"
                            prepend-icon="mdi-alert"
                            @click="showStrategyWarning(item)"
                          >
                            No cumple
                          </v-chip>
                          <v-chip 
                            v-else
                            color="success"
                            size="small"
                            prepend-icon="mdi-check"
                          >
                            Cumple
                          </v-chip>
                        </td>
                        <td class="actions-col">
                          <v-btn
                            icon="mdi-delete"
                            size="small"
                            @click="removeProduct(index)"
                            color="error"
                            variant="text"
                          ></v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-table>
              </v-card-text>
            </v-card>

            <!-- Mensaje cuando no hay productos -->
            <v-alert
              v-if="newEgreso.items.length === 0"
              type="info"
              variant="tonal"
              class="mb-4"
            >
              No hay productos agregados. Use los botones de escaneo o agregue productos manualmente.
            </v-alert>

            <!-- Panel de escaneo -->
            <v-card 
              v-if="showBarcodeScanner || isMultipleScanMode"
              variant="outlined"
              class="mb-4"
            >
              <v-card-title class="text-h6">
                {{ isMultipleScanMode ? 'Escaneo Masivo' : 'Escaneo de Productos' }}
              </v-card-title>
              <v-card-text>
                <v-textarea
                  ref="scannerInput"
                  v-model="multipleScanInput"
                  :label="isMultipleScanMode ? 'Pegue códigos aquí (uno por línea)' : 'Escanee o ingrese código'"
                  :rows="isMultipleScanMode ? 8 : 2"
                  variant="outlined"
                  density="compact"
                  @keyup.enter="isMultipleScanMode ? null : processScannedBarcode(multipleScanInput)"
                  :hint="isMultipleScanMode ? 'Pegue múltiples códigos, uno por línea' : 'Presione Enter para procesar'"
                  persistent-hint
                ></v-textarea>
                
                <div class="d-flex gap-2 mt-3">
                  <v-btn
                    color="primary"
                    @click="isMultipleScanMode ? processMultipleCodes() : processScannedBarcode(multipleScanInput)"
                    :disabled="!multipleScanInput.trim()"
                  >
                    {{ isMultipleScanMode ? 'Procesar Códigos' : 'Procesar Código' }}
                  </v-btn>
                  <v-btn
                    color="grey"
                    @click="closeBarcodeScanner"
                    variant="outlined"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn
                    v-if="isMultipleScanMode"
                    color="info"
                    @click="clearMultipleScanInput"
                    variant="outlined"
                  >
                    Limpiar
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <!-- Loading inicial -->
            <v-card variant="outlined" v-if="loadingInitialData" class="mb-4">
              <v-card-text class="text-center">
                <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
                <div>Cargando datos del sistema...</div>
              </v-card-text>
            </v-card>

            <!-- Resumen -->
            <v-card variant="outlined" v-if="newEgreso.items.length > 0">
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="8">
                    <div class="text-h6">Resumen</div>
                  </v-col>
                  <v-col cols="12" md="4" class="text-right">
                    <div>
                      <strong>Total de SKUs:</strong> {{ newEgreso.items.length }}
                    </div>
                    <div>
                      <strong>Total de Unidades:</strong> {{ totalQuantity }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeCreateModal">
            Cancelar
          </v-btn>
          <v-btn 
            color="primary"
            @click="saveEgreso"
            :loading="submitting"
            :disabled="!canSaveEgreso"
          >
            {{ isEditingEgreso ? 'Actualizar' : 'Crear Salida' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de advertencia de estrategia -->
    <v-dialog v-model="showStrategyWarningDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Advertencia de Estrategia
        </v-card-title>
        
        <v-card-text>
          <v-alert type="warning" class="mb-3">
            {{ strategyWarningMessage }}
          </v-alert>
          
          <div v-if="suggestedProduct" class="mt-3">
            <h4>Producto Sugerido:</h4>
            <v-card variant="outlined" class="mt-2">
              <v-card-text>
                <div><strong>{{ suggestedProduct.type === 'lot' ? 'Lote' : 'Fecha de Vencimiento' }}:</strong> {{ suggestedProduct.value }}</div>
                <div><strong>Cantidad Disponible:</strong> {{ suggestedProduct.available_quantity }} unidades</div>
                <div v-if="suggestedProduct.date"><strong>Fecha:</strong> {{ formatDate(suggestedProduct.date) }}</div>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="outlined" @click="ignoreStrategyWarning">
            IGNORAR ESTRATEGIA
          </v-btn>
          <v-btn color="success" @click="acceptSuggestedProduct">
            USAR SUGERIDO
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Reporte de Egreso -->
    <v-dialog v-model="showReportModal" max-width="800px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">Reporte de Salida</span>
          <v-btn
            icon="mdi-printer"
            @click="printReport"
            color="primary"
            variant="text"
            size="small"
          ></v-btn>
        </v-card-title>

        <v-card-text v-if="selectedEgreso" class="report-content">
          <!-- Información del Pedido -->
          <div class="report-header mb-4">
            <h3>Orden de Salida: {{ selectedEgreso.order_number }}</h3>
            <div class="mt-2">
              <strong>Fecha:</strong> {{ formatDate(selectedEgreso.created_at) }}<br>
              <strong>Área/Servicio:</strong> {{ getAreaServicioText(selectedEgreso.area_servicio) || 'No especificado' }}<br>
              <strong>Estrategia:</strong> {{ getStrategyText(selectedEgreso.strategy) }}<br>
              <strong>Usuario:</strong> {{ selectedEgreso.user || 'Sistema' }}<br>
              <strong>Notas:</strong> {{ selectedEgreso.notes || 'Sin notas' }}
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <!-- Tabla de Productos -->
          <h4 class="mb-3">Productos</h4>
          <v-table density="compact" class="report-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>SKU</th>
                <th>Cantidad</th>
                <th>Lote</th>
                <th>Vencimiento</th>
                <th>S/N</th>
                <th>F. Fabricación</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selectedEgreso.items" :key="item.id">
                <td>{{ item.product?.name || 'Producto desconocido' }}</td>
                <td>{{ item.product?.sku || '-' }}</td>
                <td>{{ item.quantity }}</td>
                <td>{{ item.lot_number || '-' }}</td>
                <td>{{ formatDate(item.expiry_date) || '-' }}</td>
                <td>{{ item.serial_number || '-' }}</td>
                <td>{{ formatDate(item.production_date) || '-' }}</td>
              </tr>
            </tbody>
          </v-table>

          <!-- Resumen -->
          <v-divider class="my-4"></v-divider>
          <div class="report-summary">
            <div class="d-flex justify-space-between">
              <strong>Total de SKUs:</strong>
              <span>{{ selectedEgreso.items?.length || 0 }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <strong>Total de Unidades:</strong>
              <span>{{ selectedEgreso.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0 }}</span>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="printReport" prepend-icon="mdi-printer">
            Imprimir
          </v-btn>
          <v-btn color="grey darken-1" text @click="showReportModal = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn
          color="white"
          variant="text"
          @click="snackbar.show = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'
import api from '@/api'

export default {
  name: 'Egresos',
  components: {
    ResponsiveDataTable
  },
  
  data() {
    return {
      // Filtros
      filters: {
        dateFrom: '',
        dateTo: '',
        search: ''
      },
      
      // Estado principal
      egresos: [],
      loading: false,
      
      // Datos precargados
      allProducts: [],
      inventoryData: [],
      loadingInitialData: false,
      
      // Modal crear/editar
      showCreateModal: false,
      isEditingEgreso: false,
      formValid: false,
      submitting: false,
      
      // Formulario de egreso
      newEgreso: {
        area_servicio: null,
        strategy: 'fefo',
        notes: '',
        items: []
      },
      
      // Áreas/Servicios según Normativa 247
      areasServicios: [
        { title: 'UCI - Unidad de Cuidados Intensivos', value: 'UCI' },
        { title: 'Pabellón / Quirófano', value: 'PABELLON' },
        { title: 'Urgencias', value: 'URGENCIAS' },
        { title: 'Hospitalización', value: 'HOSPITALIZACION' },
        { title: 'Consulta Externa', value: 'CONSULTA_EXTERNA' },
        { title: 'Laboratorio', value: 'LABORATORIO' },
        { title: 'Imagenología', value: 'IMAGENOLOGIA' },
        { title: 'Farmacia', value: 'FARMACIA' },
        { title: 'Esterilización', value: 'ESTERILIZACION' },
        { title: 'Maternidad', value: 'MATERNIDAD' },
        { title: 'Pediatría', value: 'PEDIATRIA' },
        { title: 'Cardiología', value: 'CARDIOLOGIA' },
        { title: 'Traumatología', value: 'TRAUMATOLOGIA' },
        { title: 'Otro', value: 'OTRO' }
      ],
      
      // Estrategias de picking
      strategyOptions: [
        { 
          value: 'libre', 
          title: 'Libre', 
          subtitle: 'Sin restricciones de estrategia' 
        },
        { 
          value: 'fifo', 
          title: 'FIFO', 
          subtitle: 'Primero en Entrar, Primero en Salir' 
        },
        { 
          value: 'fefo', 
          title: 'FEFO', 
          subtitle: 'Primero en Vencer, Primero en Salir' 
        },
        { 
          value: 'lifo', 
          title: 'LIFO', 
          subtitle: 'Último en Entrar, Primero en Salir' 
        }
      ],
      
      // Escaneo de códigos
      showBarcodeScanner: false,
      isMultipleScanMode: false,
      multipleScanInput: '',
      
      // Advertencias de estrategia
      showStrategyWarningDialog: false,
      strategyWarningMessage: '',
      suggestedProduct: null,
      currentValidatingItem: null, // Item que está siendo validado
      
      // Modal de reporte
      showReportModal: false,
      selectedEgreso: null,
      
      // Notificaciones
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      },

      // ========== PARSER GS1 PROFESIONAL - DATOS ==========
      
      // Separador FNC1 (ASCII 29)
      GS: String.fromCharCode(29),
      
      // Definición de AIs (Application Identifiers) según especificaciones GS1
      AI_DEFS: [
        // ---- Longitud fija ----
        { key: "00",  len: 18, fixed: true,  name: "SSCC" },
        { key: "01",  len: 14, fixed: true,  name: "GTIN" }, // Identificador del producto
        { key: "02",  len: 14, fixed: true,  name: "Content GTIN" },
        { key: "11",  len: 6,  fixed: true,  name: "Prod date (YYMMDD)" }, // Fecha de producción
        { key: "13",  len: 6,  fixed: true,  name: "Pack date (YYMMDD)" }, // Fecha de empaquetado
        { key: "15",  len: 6,  fixed: true,  name: "Best before (YYMMDD)" }, // Fecha de consumo preferente
        { key: "17",  len: 6,  fixed: true,  name: "Expiry (YYMMDD)" }, // Fecha de vencimiento
        { key: "310", len: 6,  fixed: true,  name: "Net weight (kg, 6d)",  wildcard: true }, // 310x
        { key: "320", len: 6,  fixed: true,  name: "Net weight (lb, 6d)", wildcard: true }, // 320x
        { key: "330", len: 6,  fixed: true,  name: "Gross weight (kg, 6d)", wildcard: true }, // 330x - GS1 REQUERIDO
        { key: "340", len: 6,  fixed: true,  name: "Gross weight (lb, 6d)", wildcard: true }, // 340x - GS1 REQUERIDO
        { key: "390", len: null, fixed: false, name: "Amount w/o curr",   wildcard: true, max: 15 }, // 390x var
        { key: "414", len: 13, fixed: true,  name: "GLN" }, // Global Location Number - GS1 REQUERIDO
        
        // ---- Longitud variable ----
        { key: "10", fixed: false, name: "Batch/Lot", max: 20 }, // Lote
        { key: "21", fixed: false, name: "Serial",    max: 20 }, // Número de serie
        { key: "22", fixed: false, name: "CPV",       max: 20 }, // Consumer Product Variant
        { key: "30",  fixed: false, name: "Qty variable measure", max: 8 },
        { key: "37",  fixed: false, name: "Count", max: 8 },
        { key: "90",  fixed: false, name: "Internal use 90", max: 30 }, // GS1 REQUERIDO
        { key: "91",  fixed: false, name: "Internal use 91", max: 30 }, // GS1 REQUERIDO
        { key: "92",  fixed: false, name: "Internal use 92", max: 30 }, // GS1 REQUERIDO  
        { key: "93",  fixed: false, name: "Internal use 93", max: 30 }, // GS1 REQUERIDO
        { key: "94",  fixed: false, name: "Internal use 94", max: 30 }, // GS1 REQUERIDO
        { key: "95",  fixed: false, name: "Internal use 95", max: 30 }, // GS1 REQUERIDO
        { key: "96",  fixed: false, name: "Internal use 96", max: 30 }, // GS1 REQUERIDO
        { key: "97",  fixed: false, name: "Internal use 97", max: 30 }, // GS1 REQUERIDO
        { key: "98",  fixed: false, name: "Internal use 98", max: 30 }, // GS1 REQUERIDO
        { key: "99",  fixed: false, name: "Internal use 99", max: 30 }, // GS1 REQUERIDO
        { key: "240", fixed: false, name: "Additional ID", max: 30 },
        { key: "241", fixed: false, name: "Customer Part #", max: 30 },
        { key: "242", fixed: false, name: "MTO variant", max: 6 },
        { key: "243", fixed: false, name: "Supplier Part #", max: 30 },
        { key: "253", fixed: false, name: "GDTI + Serial", max: 30 }, // Global Document Type Identifier - GS1 REQUERIDO
        { key: "402", len: 17, fixed: true, name: "GSIN" }, // Global Shipment Identification Number - GS1 REQUERIDO
      ]
    }
  },
  
  computed: {
    filteredEgresos() {
      let filtered = this.egresos
      
      if (this.filters.dateFrom) {
        filtered = filtered.filter(e => e.created_at >= this.filters.dateFrom)
      }
      
      if (this.filters.dateTo) {
        filtered = filtered.filter(e => e.created_at <= this.filters.dateTo)
      }
      
      return filtered
    },
    
    canSaveEgreso() {
      return this.newEgreso.area_servicio && 
             this.newEgreso.items.length > 0 && 
             this.formValid
    },
    
    totalQuantity() {
      return this.newEgreso.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
    }
  },
  
  async created() {
    await this.loadEgresos()
  },
  
  methods: {
    // Cargar datos
    async loadInitialData() {
      if (this.loadingInitialData) return
      
      try {
        this.loadingInitialData = true
        console.log('🔄 Cargando datos iniciales...')
        
        // Cargar productos y inventario en paralelo
        const [productsResponse, inventoryResponse] = await Promise.all([
          api.get('/products'),
          api.get('/inventory')
        ])
        
        this.allProducts = productsResponse.data || []
        this.inventoryData = inventoryResponse.data || []
        
        console.log(`✅ Datos cargados: ${this.allProducts.length} productos, ${this.inventoryData.length} items de inventario`)
        
      } catch (error) {
        console.error('❌ Error cargando datos iniciales:', error)
        this.showError('Error al cargar datos del sistema')
      } finally {
        this.loadingInitialData = false
      }
    },
    
    async loadEgresos() {
      try {
        this.loading = true
        const response = await api.get('/egresos/egress-orders', {
          params: this.filters
        })
        this.egresos = response.data || []
      } catch (error) {
        console.error('Error loading egresos:', error)
        this.showError('Error al cargar órdenes de egreso')
      } finally {
        this.loading = false
      }
    },
    
    // Modal handlers
    async showNewEgresoModal() {
      this.resetEgresoForm()
      this.isEditingEgreso = false
      this.showCreateModal = true
      
      // Cargar datos necesarios para el escaneo
      await this.loadInitialData()
    },
    
    closeCreateModal() {
      this.showCreateModal = false
      this.resetEgresoForm()
    },
    
    resetEgresoForm() {
      this.newEgreso = {
        area_servicio: null,
        strategy: 'fefo',
        notes: '',
        items: []
      }
      this.formValid = false
      this.closeBarcodeScanner()
    },
    
    // Strategy handlers
    onStrategyChanged(strategy) {
      console.log('Strategy changed:', strategy)
      // Revalidar todos los productos cuando cambie la estrategia
      if (this.newEgreso.items.length > 0) {
        this.validateAllProductStrategies()
      }
    },
    
    async validateAllProductStrategies() {
      for (let item of this.newEgreso.items) {
        await this.validateProductStrategy(item)
      }
    },
    
    async validateProductStrategy(item) {
      if (this.newEgreso.strategy === 'libre') {
        item.strategy_compliant = true
        item.strategy_warning = null
        return
      }
      
      try {
        console.log(`🔍 Validando estrategia ${this.newEgreso.strategy} para producto ${item.product_id}`)
        
        // Obtener items disponibles del producto
        const response = await api.get(`/inventory/products/${item.product_id}/available-items`)
        const availableItems = response.data
        
        console.log('📦 Items disponibles:', availableItems)
        
        let compliant = true
        let warning = null
        let suggestedItem = null
        
        if (this.newEgreso.strategy === 'fifo') {
          ({ compliant, warning, suggestedItem } = this.validateFIFOStrategy(item, availableItems))
        } else if (this.newEgreso.strategy === 'lifo') {
          ({ compliant, warning, suggestedItem } = this.validateLIFOStrategy(item, availableItems))
        } else if (this.newEgreso.strategy === 'fefo') {
          // Solo validar FEFO si hay fecha de vencimiento
          if (!item.expiry_date) {
            console.log('⚠️ FEFO: No hay fecha de vencimiento, saltando validación')
            compliant = true
            warning = null
            suggestedItem = null
          } else {
            ({ compliant, warning, suggestedItem } = this.validateFEFOStrategy(item, availableItems))
          }
        }
        
        item.strategy_compliant = compliant
        item.strategy_warning = warning
        item.suggested_product = suggestedItem
        
        if (!compliant) {
          console.log(`⚠️ Estrategia no cumplida: ${warning}`)
        } else {
          console.log(`✅ Estrategia cumplida para ${this.newEgreso.strategy}`)
        }
        
      } catch (error) {
        console.error('❌ Error validando estrategia:', error)
        item.strategy_compliant = true // En caso de error, permitir el producto
      }
    },
    
    // Función auxiliar para enfocar el input del escáner
    focusScannerInput() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.scannerInput && this.$refs.scannerInput.$el) {
            const textarea = this.$refs.scannerInput.$el.querySelector('textarea')
            if (textarea) {
              textarea.focus()
            }
          }
        }, 100)
      })
    },
    
    // Barcode scanning
    startSingleScan() {
      this.isMultipleScanMode = false
      this.showBarcodeScanner = true
      this.multipleScanInput = ''
      
      // Auto-focus en el input después de abrir el panel
      this.focusScannerInput()
    },
    
    startMultipleScan() {
      this.isMultipleScanMode = true
      this.showBarcodeScanner = true
      this.multipleScanInput = ''
    },
    
    closeBarcodeScanner() {
      this.showBarcodeScanner = false
      this.isMultipleScanMode = false
      this.multipleScanInput = ''
    },
    
    clearMultipleScanInput() {
      this.multipleScanInput = ''
    },
    
    async processMultipleCodes() {
      const codes = this.multipleScanInput.split('\n').filter(code => code.trim())
      
      for (const code of codes) {
        await this.processScannedBarcode(code.trim())
      }
      
      this.closeBarcodeScanner()
    },
    
    async processScannedBarcode(code) {
      if (!code || !code.trim()) return
      
      console.log(`🔍 Procesando código de barras: ${code}`)
      
      // Verificar que los datos estén cargados
      if (this.allProducts.length === 0) {
        console.log('⚠️ Los datos no están cargados, cargando...')
        await this.loadInitialData()
      }
      
      try {
        // Analizar el código
        const analysis = this.analyzeBarcode(code.trim())
        console.log('📊 Análisis del código:', analysis)
        
        let product = null
        let productData = {}
        
        if (analysis.type === 'GS1') {
          console.log(`🏷️ Código GS1 detectado, GTIN: ${analysis.gtin}`)
          // Buscar producto por GTIN
          product = this.findProductByGTIN(analysis.gtin)
          productData = analysis.variableData || {}
          console.log('📦 Datos variables extraídos:', productData)
        } else {
          console.log(`📋 Código estándar detectado: ${code}`)
          // Buscar producto por código de barras normal
          product = this.findProductByBarcode(code)
        }
        
        if (product) {
          console.log(`✅ Producto encontrado: ${product.name} (ID: ${product.id})`)
          await this.addScannedProduct(product, productData, code)
        } else {
          console.log(`❌ Producto NO encontrado para código: ${code}`)
          console.log(`🔍 Productos disponibles: ${this.allProducts.length}`)
          
          // En lugar de mostrar error, mostrar información del código escaneado
          if (analysis.type === 'GS1' && productData && Object.keys(productData).length > 0) {
            // Es un código GS1 válido pero no comercial - mostrar información
            this.showCodeInformation(analysis, productData)
          } else if (analysis.type === 'GS1' && analysis.gtin) {
            // Es GS1 pero solo con GTIN - mostrar información básica
            this.showBasicCodeInformation(analysis)
          } else {
            // Código no GS1 o sin datos válidos - mostrar error tradicional
            this.showError(`Producto no encontrado para el código: ${code}`)
          }
        }
        
      } catch (error) {
        console.error('❌ Error procesando código:', error)
        this.showError(`Error procesando código: ${code}`)
      }
      
      // Limpiar input en modo individual
      if (!this.isMultipleScanMode) {
        this.multipleScanInput = ''
      }
    },
    
    // ========== MÉTODOS DE ANÁLISIS DE CÓDIGOS DE BARRAS ==========
    
    // Detectar tipo de código de barras y extraer GTIN si es GS1
    analyzeBarcode(barcode) {
      if (!barcode || barcode.trim() === '') {
        return { type: 'Sin código', gtin: null, original: barcode, variableData: null }
      }
      
      const cleaned = barcode.trim()
      
      console.log('🧪 Analizando código:', {
        original: cleaned,
        length: cleaned.length,
        startsWithAI01: cleaned.startsWith('01'),
        hasParens: cleaned.includes('(')
      })
      
      // Detección GS1 mejorada - según especificaciones del usuario
      const isGS1 = cleaned.includes('(') || 
                   (cleaned.startsWith('01') && cleaned.length > 16) ||
                   (cleaned.length > 20 && /^\d/.test(cleaned)) ||
                   (cleaned.includes(String.fromCharCode(29))) // Incluir códigos con separador FNC1
      
      if (isGS1) {
        const { gtin, variableData } = this.extractGTINAndVariableData(cleaned)
        const hasVariableData = variableData && Object.keys(variableData).length > 0
        
        console.log('✅ Identificado como GS1:', {
          gtin: gtin,
          hasVariableData: hasVariableData,
          variableData: variableData
        })
        
        return { 
          type: 'GS1', 
          gtin: gtin,
          original: cleaned,
          variableData: variableData,
          hasVariableData: hasVariableData
        }
      }
      
      // Solo evaluar códigos de longitud fija si NO es GS1
      // EAN-13 (exactamente 13 dígitos)
      if (/^\d{13}$/.test(cleaned)) {
        console.log('📊 Identificado como EAN-13:', cleaned)
        return { type: 'EAN-13', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // UPC-A (exactamente 12 dígitos)
      if (/^\d{12}$/.test(cleaned)) {
        console.log('📊 Identificado como UPC-A:', cleaned)
        return { type: 'UPC-A', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // ITF-14 (exactamente 14 dígitos)
      if (/^\d{14}$/.test(cleaned)) {
        console.log('📊 Identificado como ITF-14:', cleaned)
        return { type: 'ITF-14', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // Otros códigos
      console.log('📋 Código no identificado específicamente:', cleaned)
      return { type: 'Other', barcode: cleaned, original: cleaned, variableData: null }
    },
    
    // Extraer GTIN y datos variables de código GS1
    extractGTINAndVariableData(gs1Code) {
      if (!gs1Code) return { gtin: null, variableData: null }
      
      let cleaned = gs1Code.replace(/[()]/g, '') // Remover paréntesis si existen
      cleaned = this.normalizeFNC1(cleaned)      // Normalizar FNC1 desde escáner DS2278
      let gtin = null
      let variableData = {}
      
      console.log('🔧 Extrayendo GTIN de:', cleaned)
      console.log('🔧 Código después de normalizar FNC1:', cleaned.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      // Log del código normalizado para debug
      if (window.debugGS1) {
        console.log('🚨 DEBUG GS1: Código detectado')
        console.log('Código completo:', cleaned)
        console.log('¿Contiene FNC1?', cleaned.includes(this.GS))
      }
      
      try {
        // Método mejorado según especificaciones del usuario:
        // "Si el código empieza con 01 los siguientes 14 caracteres son el GTIN"
        
        if (cleaned.startsWith('01') && cleaned.length >= 16) {
          // Extraer exactamente los siguientes 14 caracteres después de "01"
          gtin = cleaned.substring(2, 16) // Posiciones 2-15 (14 dígitos del GTIN)
          const remainingData = cleaned.substring(16) // Todo lo que viene después del GTIN
          
          console.log('✅ GTIN extraído según especificación (01 + 14 chars):', gtin)
          console.log('🔍 Datos restantes para parsing:', remainingData)
          
          if (remainingData && remainingData.length > 0) {
            // Parsear datos variables que vienen después del GTIN
            variableData = this.parseGS1VariableData(remainingData)
          }
        } else {
          // Métodos alternativos para otros casos
          const gtinMatch = cleaned.match(/^01(\d{14})(.*)/)
          if (gtinMatch) {
            gtin = gtinMatch[1] // GTIN de 14 dígitos
            const remainingData = gtinMatch[2] // Datos adicionales después del GTIN
            
            console.log('✅ GTIN extraído (regex):', gtin)
            console.log('🔍 Datos restantes para parsing:', remainingData)
            
            if (remainingData && remainingData.length > 0) {
              variableData = this.parseGS1VariableData(remainingData)
            }
          } else if (/^\d+$/.test(cleaned) && cleaned.length > 14) {
            // Si es un código largo sin AI visible, intentar extraer los primeros 14 dígitos
            gtin = cleaned.substring(0, 14)
            console.log('⚠️ GTIN extraído por longitud:', gtin)
          }
        }
        
        // Si no se puede extraer GTIN, usar el código original
        if (!gtin) {
          console.log('❌ No se pudo extraer GTIN, usando código completo')
          gtin = cleaned
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 code:', error)
        gtin = cleaned
      }
      
      console.log('🎯 Resultado final - GTIN:', gtin, 'Variables:', variableData)
      return { gtin, variableData }
    },
    
    // ========== PARSER GS1 PROFESIONAL ==========
    
    // Función de utilidad para parsear fechas GS1 (YYMMDD → YYYY-MM-DD)
    parseGS1Date(dateValue, aiType = 'genérica') {
      if (!dateValue || dateValue.length !== 6) {
        console.warn(`⚠️ Fecha GS1 inválida (${aiType}):`, dateValue)
        return null
      }
      
      const yy = dateValue.substring(0, 2)
      const mm = dateValue.substring(2, 4)
      let dd = dateValue.substring(4, 6)
      
      // Validar rangos básicos
      const monthNum = parseInt(mm)
      const dayNum = parseInt(dd)
      
      if (monthNum < 1 || monthNum > 12) {
        console.warn(`⚠️ Mes inválido en fecha GS1 (${aiType}):`, dateValue, `→ mm=${mm}`)
        return null
      }
      
      // Corregir día 00 a 01 para fechas válidas
      if (dd === '00') {
        dd = '01'
        console.log(`📅 Corrigiendo día 00 → 01 en fecha ${aiType}: ${dateValue} → ${yy}${mm}${dd}`)
      }
      
      // Validar día después de corrección
      if (dayNum < 0 || dayNum > 31) {
        console.warn(`⚠️ Día inválido en fecha GS1 (${aiType}):`, dateValue, `→ dd=${dd}`)
        return null
      }
      
      const parsedDate = `20${yy}-${mm}-${dd}T00:00:00`
      console.log(`📅 Fecha GS1 parseada (${aiType}): ${dateValue} → ${parsedDate}`)
      return parsedDate
    },
    
    // Construir índices de búsqueda
    buildAIIndex() {
      const exact = new Map()
      const wild = []
      
      for (const d of this.AI_DEFS) {
        if (d.wildcard) {
          const re = new RegExp("^" + d.key + "\\d")
          wild.push({ re, def: d })
        } else {
          exact.set(d.key, d)
        }
      }
      
      return { exact, wild }
    },
    
    // Encontrar AI en posición específica
    findAI(s, pos, aiIndex) {
      const chunk4 = s.slice(pos, pos + 4)
      const chunk3 = s.slice(pos, pos + 3)
      const chunk2 = s.slice(pos, pos + 2)
      
      console.log(`🔍 findAI en pos ${pos}: chunk2="${chunk2}", chunk3="${chunk3}", chunk4="${chunk4}"`)
      
      // Wildcards primero (310x, 320x, 390x, etc)
      for (const { re, def } of aiIndex.wild) {
        if (re.test(s.slice(pos, pos + 4))) {
          console.log(`✅ AI wildcard encontrado: ${s.slice(pos, pos + 4)} (base: ${def.key})`)
          return { key: s.slice(pos, pos + 4), base: def.key, def }
        }
      }
      
      // Exactos 4 → 3 → 2 dígitos
      if (aiIndex.exact.has(chunk4)) {
        console.log(`✅ AI exacto 4 dígitos encontrado: ${chunk4}`)
        return { key: chunk4, base: chunk4, def: aiIndex.exact.get(chunk4) }
      }
      if (aiIndex.exact.has(chunk3)) {
        console.log(`✅ AI exacto 3 dígitos encontrado: ${chunk3}`)
        return { key: chunk3, base: chunk3, def: aiIndex.exact.get(chunk3) }
      }
      if (aiIndex.exact.has(chunk2)) {
        console.log(`✅ AI exacto 2 dígitos encontrado: ${chunk2}`)
        return { key: chunk2, base: chunk2, def: aiIndex.exact.get(chunk2) }
      }
      
      console.log(`❌ No se encontró AI en pos ${pos}`)
      return null
    },
    
    // Validar si una secuencia AI es realmente válida - Simplificado con regla de AI único
    isValidAISequence(aiMatch, remainingData) {
      if (!aiMatch || !remainingData) return false
      
      const { def } = aiMatch
      
      console.log(`🔍 Validando AI ${aiMatch.key} con datos: "${remainingData.substring(0, 15)}..."`)
      
      // Para AIs de longitud fija, verificar que hay suficiente contenido
      if (def.fixed) {
        const requiredLength = def.len
        if (remainingData.length < requiredLength) {
          console.log(`❌ AI ${aiMatch.key} requiere ${requiredLength} chars, solo quedan ${remainingData.length}`)
          return false
        }
        
        // Para fechas, verificar formato básico (6 dígitos)
        if (['11', '13', '15', '17'].includes(def.key) && !/^\d{6}/.test(remainingData)) {
          console.log(`❌ AI fecha ${aiMatch.key} no tiene formato válido:`, remainingData.substring(0, 6))
          return false
        }
        
        console.log(`✅ AI fijo ${aiMatch.key} válido`)
        return true
      }
      
      // Para AIs de longitud variable
      if (remainingData.length === 0) {
        console.log(`❌ AI variable ${aiMatch.key} sin contenido`)
        return false
      }
      
      // Validación especial para AI (01) GTIN
      if (aiMatch.base === '01') {
        if (remainingData.length < 14 || !/^\d{14}/.test(remainingData)) {
          console.log(`❌ AI (01) debe tener 14 dígitos, encontrado:`, remainingData.substring(0, 14))
          return false
        }
        
        const afterGTIN = remainingData.substring(14)
        if (afterGTIN.length > 0) {
          const nextPossibleAI = afterGTIN.substring(0, 2)
          const validNextAIs = ['10', '11', '13', '15', '17', '21', '30', '37', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99', '240', '241', '242', '243', '253', '310', '320', '330', '340', '390', '414', '402']
          
          if (!validNextAIs.includes(nextPossibleAI) && afterGTIN.length > 2) {
            console.log(`❌ AI (01) seguido de secuencia inválida: "${nextPossibleAI}"`)
            return false
          }
        }
      }
      
      // Para AI (10) lote, verificar contenido mínimo razonable
      if (aiMatch.base === '10') {
        if (remainingData.length <= 2) {
          console.log(`❌ AI (10) con contenido muy corto: "${remainingData}"`)
          return false
        }
      }
      
      console.log(`✅ AI variable ${aiMatch.key} válido`)
      return true
    },
    
    // Normalizar distintos formatos de FNC1 a un único \x1D
    normalizeFNC1(s) {
      if (!s) return s;
      
      if (window.debugGS1) {
        console.log('🔧 normalizeFNC1 input:', s);
      }
      
      let result = s
        .replace(/\u001D/g, this.GS)                   // FNC1 real (ASCII 29)
        .replace(/<FNC1>|<GS>|\|/gi, this.GS);         // si usaste texto visible
      
      // Parche mejorado: el lector está enviando "0029". 
      // Convertir SOLO cuando aparece como separador entre un valor y el siguiente AI válido
      const before = result;
      result = result.replace(/(\d{3,30})0029(?=(11|13|15|17|10|21|22|240|241|242|243|30|37))/g, `$1${this.GS}`);
      
      if (before !== result) {
        console.log('✅ Se aplicaron reemplazos de 0029:', {
          before: before,
          after: result.replace(new RegExp(this.GS, 'g'), '<FNC1>')
        });
      } else {
        console.log('❌ NO se aplicaron reemplazos de 0029');
        
        // Intentar método alternativo: buscar patrones específicos
        if (s.includes('0029')) {
          console.log('🔧 Intentando método alternativo para 0029...');
          // Buscar patrones más específicos
          const alternativePattern = /(\d{3,30})0029(\d{2,4})/g;
          let match;
          while ((match = alternativePattern.exec(s)) !== null) {
            const [full, beforeValue, afterValue] = match;
            console.log('📍 Patrón encontrado:', {
              full: full,
              before: beforeValue,
              after: afterValue,
              afterLength: afterValue.length
            });
            
            // Verificar si el "after" parece un AI válido
            const commonAIs = ['10', '11', '13', '15', '17', '21', '30', '37'];
            const possibleAI = afterValue.substring(0, 2);
            if (commonAIs.includes(possibleAI)) {
              console.log('✅ Detectado AI válido:', possibleAI);
              result = result.replace(full, `${beforeValue}${this.GS}${afterValue}`);
            }
          }
        }
      }
      
      console.log('🎯 normalizeFNC1 result:', result.replace(new RegExp(this.GS, 'g'), '<FNC1>'));
      return result;
    },

    // Parser GS1 completo con regla: cada AI aparece UNA SOLA VEZ
    parseGS1(raw) {
      if (!raw) return { items: [], warnings: ["Empty input"] }
      
      // Normalización mejorada para DS2278
      let s = String(raw).trim()
      s = s.replace(/^\](C1|e0|E0|d2|Q3)/i, "")   // quitar prefijos de simbología
      s = this.normalizeFNC1(s)                   // normalizar FNC1 desde escáner DS2278
      s = s.replace(/<FNC1>|<GS>/gi, this.GS)    // algunos lectores usan textos literales
      
      console.log('🔍 Parseando GS1 completo:', s.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      const aiIndex = this.buildAIIndex()
      const items = []
      const warnings = []
      const processedAIs = new Set() // Registro de AIs ya procesados
      let i = 0
      
      while (i < s.length) {
        console.log(`🔍 Posición ${i}: Analizando "${s.slice(i, i+8)}..."`)
        
        // Saltar separadores FNC1
        if (s[i] === this.GS) { 
          i++
          console.log('⚡ FNC1 encontrado y saltado')
          continue 
        }
        
        const m = this.findAI(s, i, aiIndex)
        if (!m) {
          warnings.push(`AI desconocido en posición ${i}: "${s.slice(i, i+4)}..."`)
          console.log(`⚠️ AI desconocido en posición ${i}:`, s.slice(i, i+4))
          console.log(`🔍 Caracteres siguientes: "${s.slice(i, i+8)}"`)
          break
        }
        
        const { key, base, def } = m
        i += key.length
        
        // REGLA FUNDAMENTAL: Verificar si este AI ya fue procesado
        if (processedAIs.has(base)) {
          console.log(`⚠️ AI ${base} duplicado detectado - IGNORANDO (regla: un AI por código)`)
          warnings.push(`AI duplicado ${base} ignorado`)
          break // O continuar hasta el final del string como contenido del AI anterior
        }
        
        console.log(`📊 Procesando AI ${key} (${def.name}) - Primer encuentro ✓`)
        processedAIs.add(base) // Marcar como procesado
        
        let value = ""
        let ambiguous = false
        
        if (def.fixed) {
          // Longitud fija
          const need = def.len
          if (i + need > s.length) {
            value = s.slice(i)
            warnings.push(`Valor incompleto para (${key}) esperaba ${need} chars.`)
            i = s.length
          } else {
            value = s.slice(i, i + need)
            i += need
          }
        } else {
          // Longitud variable: SOLO cortar con FNC1 real o límite máximo
          const max = def.max ?? 30
          let j = i
          let cutByFNC1 = false
          
          while (j < s.length && (j - i) < max) {
            // REGLA ESTRICTA: Solo cortar con FNC1 real
            if (s[j] === this.GS) { 
              console.log(`✂️ FNC1 encontrado, cortando en posición ${j}`)
              cutByFNC1 = true
              break 
            }
            j++
          }
          
          // Si no hay FNC1, tomar todo hasta el límite máximo o fin del string
          console.log(`📏 Campo variable sin FNC1: tomando hasta posición ${j} (${j-i} chars)`)
          
          // OPCIONAL: Buscar AI solo si llegamos al límite máximo sin FNC1
          if ((j - i) >= max && j < s.length) {
            // Solo en este caso, buscar AI como posible separador
            for (let k = Math.max(i + 3, j - 10); k <= j + 2 && k < s.length - 1; k++) {
              const next = this.findAI(s, k, aiIndex)
              if (next && !processedAIs.has(next.base)) {
                const remainingAfterAI = s.slice(k + next.key.length)
                
                console.log(`🔍 AI potencial en límite máximo "${next.key}" en posición ${k}`)
                
                if (this.isValidAISequence(next, remainingAfterAI)) {
                  console.log(`✂️ CORTANDO por límite máximo + AI válido: "${next.key}"`)
                  j = k
                  break
                }
              }
            }
          }
          
          value = s.slice(i, j)
          i = j
          if (s[i] === this.GS) i++ // Consumir FNC1 si lo había
          
          // Detectar ambigüedad solo si no fue cortado por FNC1
          if (!cutByFNC1) {
            const nxt = this.findAI(s, i, aiIndex)
            if (nxt && nxt.def && !nxt.def.fixed && !processedAIs.has(nxt.base)) {
              ambiguous = true
            }
          }
        }
        
        console.log(`✅ ${def.name} extraído: "${value}"${ambiguous ? ' (ambiguous)' : ''}`)
        items.push({ ai: key, aiBase: base, name: def.name, value, ambiguous })
      }
      
      console.log(`🎯 AIs procesados: ${Array.from(processedAIs).join(', ')}`)
      return { items, warnings }
    },
    
    parseGS1VariableData(remainingData) {
      const variableData = {}
      
      console.log('🔍 Parseando datos GS1 con parser profesional:', remainingData)
      
      try {
        // Usar el parser completo
        const result = this.parseGS1(remainingData)
        
        if (result.warnings.length > 0) {
          console.warn('⚠️ Advertencias GS1:', result.warnings)
        }
        
        // Variables temporales para fechas
        let expiryDate = null
        let productionDate = null
        let packagingDate = null
        let bestBeforeDate = null
        
        // Convertir items a formato esperado por el resto del código
        for (const item of result.items) {
          switch (item.aiBase) {
            case '10':
              variableData.lot_number = item.value
              break
            case '11':
              // Fecha de producción YYMMDD → YYYY-MM-DD
              productionDate = this.parseGS1Date(item.value, 'producción')
              if (productionDate) {
                variableData.production_date = productionDate
              }
              break
            case '13':
              // Fecha de empaquetado YYMMDD → YYYY-MM-DD
              packagingDate = this.parseGS1Date(item.value, 'empaquetado')
              if (packagingDate) {
                variableData.packaging_date = packagingDate
              }
              break
            case '15':
              // Fecha de consumo preferente YYMMDD → YYYY-MM-DD
              bestBeforeDate = this.parseGS1Date(item.value, 'consumo preferente')
              if (bestBeforeDate) {
                variableData.best_before_date = bestBeforeDate
              }
              break
            case '17':
              // Fecha de vencimiento YYMMDD → YYYY-MM-DD
              expiryDate = this.parseGS1Date(item.value, 'vencimiento')
              if (expiryDate) {
                variableData.expiry_date = expiryDate
              }
              break
            case '21':
              variableData.serial_number = item.value
              break
            case '30':
              variableData.quantity = item.value
              break
            case '37':
              variableData.count = item.value
              break
            // ========== NUEVOS AIs GS1 - FASE 2 ==========
            case '00':
              variableData.sscc = item.value
              break
            case '253':
              variableData.gdti = item.value
              break
            case '310':
            case '311':
            case '312':
            case '313':
            case '314':
            case '315':
              // Peso neto en kg (310X)
              variableData.net_weight_kg = parseFloat(item.value) || null
              break
            case '320':
            case '321':
            case '322':
            case '323':
            case '324':
            case '325':
              // Peso neto en lb (320X)
              variableData.net_weight_lb = parseFloat(item.value) || null
              break
            case '330':
            case '331':
            case '332':
            case '333':
            case '334':
            case '335':
              // Peso bruto en kg (330X)
              variableData.gross_weight_kg = parseFloat(item.value) || null
              break
            case '340':
            case '341':
            case '342':
            case '343':
            case '344':
            case '345':
              // Peso bruto en lb (340X)
              variableData.gross_weight_lb = parseFloat(item.value) || null
              break
            case '402':
              variableData.gsin = item.value
              break
            case '414':
              variableData.gln = item.value
              break
            // Campos personalizados (90-99)
            case '90':
              variableData.custom_field_90 = item.value
              break
            case '91':
              variableData.custom_field_91 = item.value
              break
            case '92':
              variableData.custom_field_92 = item.value
              break
            case '93':
              variableData.custom_field_93 = item.value
              break
            case '94':
              variableData.custom_field_94 = item.value
              break
            case '95':
              variableData.custom_field_95 = item.value
              break
            case '96':
              variableData.custom_field_96 = item.value
              break
            case '97':
              variableData.custom_field_97 = item.value
              break
            case '98':
              variableData.custom_field_98 = item.value
              break
            case '99':
              variableData.custom_field_99 = item.value
              break
          }
        }
        
        // Lógica de prioridad para fechas según especificaciones del usuario:
        // Si no hay fecha de vencimiento (17), usar otras fechas en el campo de vencimiento
        if (!expiryDate) {
          if (bestBeforeDate) {
            // Prioridad 1: Best before date (15)
            variableData.expiry_date = bestBeforeDate
            console.log('📅 Usando fecha de consumo preferente (15) como vencimiento:', bestBeforeDate)
          } else if (packagingDate) {
            // Prioridad 2: Packaging date (13) 
            variableData.expiry_date = packagingDate
            console.log('📅 Usando fecha de empaquetado (13) como vencimiento:', packagingDate)
          } else if (productionDate) {
            // Prioridad 3: Production date (11)
            variableData.expiry_date = productionDate
            console.log('📅 Usando fecha de producción (11) como vencimiento:', productionDate)
          }
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 variable data:', error)
      }
      
      console.log('🎯 Datos variables extraídos:', variableData)
      return variableData
    },
    
    findProductByGTIN(gtin) {
      console.log(`🔍 Buscando producto por GTIN: ${gtin}`)
      const product = this.allProducts.find(p => p.barcode === gtin)
      if (product) {
        console.log(`✅ Producto encontrado por GTIN: ${product.name} (ID: ${product.id})`)
      } else {
        console.log(`❌ Producto NO encontrado por GTIN: ${gtin}`)
      }
      return product || null
    },
    
    findProductByBarcode(barcode) {
      console.log(`🔍 Buscando producto por código de barras: ${barcode}`)
      const product = this.allProducts.find(p => p.barcode === barcode)
      if (product) {
        console.log(`✅ Producto encontrado por código: ${product.name} (ID: ${product.id})`)
      } else {
        console.log(`❌ Producto NO encontrado por código: ${barcode}`)
      }
      return product || null
    },
    
    async addScannedProduct(product, variableData, originalCode) {
      console.log(`📦 Agregando producto escaneado: ${product.name} (ID: ${product.id})`)
      
      // 1. VALIDAR INVENTARIO DISPONIBLE
      const hasInventory = await this.validateProductInventory(product.id)
      if (!hasInventory) {
        this.showError(`El producto "${product.name}" no tiene inventario disponible`)
        return
      }
      
      // 2. VERIFICAR DUPLICADOS (mismo SKU + mismo código completo)
      const duplicateIndex = this.findDuplicateItem(product.sku, originalCode, variableData)
      if (duplicateIndex !== -1) {
        // Incrementar cantidad del item existente
        this.newEgreso.items[duplicateIndex].quantity += 1
        this.showSuccess(`Cantidad actualizada: ${product.name} (${this.newEgreso.items[duplicateIndex].quantity} unidades)`)
        console.log(`♻️ Duplicado detectado - Incrementando cantidad a ${this.newEgreso.items[duplicateIndex].quantity}`)
        return
      }

      // 3. CREAR NUEVO ITEM
      const item = {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        quantity: parseInt(variableData.quantity) || 1,
        lot_number: variableData.lot_number || null,
        expiry_date: variableData.expiry_date || null,
        production_date: variableData.production_date || null,
        serial_number: variableData.serial_number || null,
        barcode_scanned: originalCode,
        gtin: variableData.gtin || null,
        strategy_compliant: true,
        strategy_warning: null,
        // ========== NUEVOS CAMPOS GS1 - FASE 2 ==========
        sscc: variableData.sscc || null,
        pack_date: variableData.pack_date || null,
        best_before_date: variableData.best_before_date || null,
        gln: variableData.gln || null,
        gsin: variableData.gsin || null,
        gdti: variableData.gdti || null,
        net_weight_kg: variableData.net_weight_kg || null,
        net_weight_lb: variableData.net_weight_lb || null,
        gross_weight_kg: variableData.gross_weight_kg || null,
        gross_weight_lb: variableData.gross_weight_lb || null,
        // Campos personalizados (90-99)
        custom_field_90: variableData.custom_field_90 || null,
        custom_field_91: variableData.custom_field_91 || null,
        custom_field_92: variableData.custom_field_92 || null,
        custom_field_93: variableData.custom_field_93 || null,
        custom_field_94: variableData.custom_field_94 || null,
        custom_field_95: variableData.custom_field_95 || null,
        custom_field_96: variableData.custom_field_96 || null,
        custom_field_97: variableData.custom_field_97 || null,
        custom_field_98: variableData.custom_field_98 || null,
        custom_field_99: variableData.custom_field_99 || null
      }
      
      console.log(`🏷️ Item creado:`, {
        sku: item.product_sku,
        quantity: item.quantity,
        lot: item.lot_number,
        expiry: item.expiry_date,
        serial: item.serial_number,
        production: item.production_date
      })
      
      // 4. VALIDAR ESTRATEGIA
      await this.validateProductStrategy(item)
      
      // 5. AGREGAR AL LISTADO
      this.newEgreso.items.push(item)
      
      // 6. MOSTRAR NOTIFICACIÓN
      this.showSuccess(`Producto agregado: ${product.name}`)
      
      // 7. SI NO CUMPLE LA ESTRATEGIA, MOSTRAR ADVERTENCIA
      if (!item.strategy_compliant) {
        this.showStrategyWarning(item)
      }
    },
    
    // Validar si el producto tiene inventario disponible
    async validateProductInventory(productId) {
      console.log(`🔍 Validando inventario para producto ID: ${productId}`)
      
      try {
        // Buscar en inventario local primero
        const inventoryItems = this.inventoryData.filter(item => item.product.id === productId)
        
        if (inventoryItems.length === 0) {
          console.log(`❌ No se encontró inventario local para producto ${productId}`)
          return false
        }
        
        // Verificar si hay cantidad disponible
        const totalQuantity = inventoryItems.reduce((sum, item) => sum + item.quantity, 0)
        
        if (totalQuantity <= 0) {
          console.log(`❌ Inventario sin cantidad disponible para producto ${productId}: ${totalQuantity}`)
          return false
        }
        
        console.log(`✅ Inventario disponible para producto ${productId}: ${totalQuantity} unidades`)
        return true
        
      } catch (error) {
        console.error(`❌ Error validando inventario para producto ${productId}:`, error)
        return false
      }
    },
    
    // Buscar item duplicado basado en SKU y código completo
    findDuplicateItem(sku, originalCode, variableData) {
      console.log(`🔍 Buscando duplicados para SKU: ${sku}, Código: ${originalCode}`)
      
      for (let i = 0; i < this.newEgreso.items.length; i++) {
        const item = this.newEgreso.items[i]
        
        // Comparar SKU y código de barras exacto
        if (item.product_sku === sku && item.barcode_scanned === originalCode) {
          // Para códigos GS1, también verificar que coincidan todos los AIs
          const sameVariableData = this.compareVariableData(item, variableData)
          
          if (sameVariableData) {
            console.log(`♻️ Duplicado encontrado en índice ${i}:`, {
              sku: item.product_sku,
              código: item.barcode_scanned,
              cantidad_actual: item.quantity
            })
            return i
          }
        }
      }
      
      console.log(`✅ No se encontraron duplicados para SKU: ${sku}`)
      return -1
    },
    
    // Comparar datos variables para determinar si son el mismo producto exacto
    compareVariableData(existingItem, newVariableData) {
      // Comparar campos clave que determinan si es el mismo producto exacto
      const fieldsToCompare = ['lot_number', 'expiry_date', 'production_date', 'serial_number']
      
      for (const field of fieldsToCompare) {
        const existingValue = existingItem[field] || null
        const newValue = newVariableData[field] || null
        
        // Si ambos valores existen y son diferentes, no es duplicado
        if (existingValue && newValue && existingValue !== newValue) {
          console.log(`🔍 Diferencia en ${field}: "${existingValue}" vs "${newValue}"`)
          return false
        }
        
        // Si solo uno tiene valor, no es duplicado
        if ((existingValue && !newValue) || (!existingValue && newValue)) {
          console.log(`🔍 Diferencia en ${field}: uno tiene valor y el otro no`)
          return false
        }
      }
      
      console.log(`✅ Los datos variables coinciden - es el mismo producto exacto`)
      return true
    },

    addProductManually() {
      // TODO: Implementar modal para agregar producto manualmente
      this.showInfo('Funcionalidad pendiente de implementar')
    },
    
    removeProduct(index) {
      this.newEgreso.items.splice(index, 1)
    },
    
    // Advertencias de estrategia
    showStrategyWarning(item) {
      this.strategyWarningMessage = item.strategy_warning
      this.suggestedProduct = item.suggested_product
      this.currentValidatingItem = item // Guardar referencia del item que está siendo validado
      this.showStrategyWarningDialog = true
    },
    
    closeStrategyWarningDialog() {
      this.showStrategyWarningDialog = false
      this.strategyWarningMessage = ''
      this.suggestedProduct = null
      this.currentValidatingItem = null
    },
    
    ignoreStrategyWarning() {
      console.log('🚫 [STRATEGY] Usuario ignora advertencia de estrategia')
      // Mantener el producto original sin cambios (ya está en la tabla)
      this.currentValidatingItem.strategy_compliant = false // Marcar como no cumple pero permitir
      this.closeStrategyWarningDialog()
      this.showInfo('Estrategia ignorada. El producto se mantendrá tal como fue escaneado.')
      
      // Re-enfocar el input del escáner
      this.focusScannerInput()
    },
    
    async acceptSuggestedProduct() {
      console.log('✅ [STRATEGY] Usuario acepta producto sugerido')
      
      if (!this.currentValidatingItem || !this.suggestedProduct) {
        this.showError('Error: No se encontró información del producto sugerido')
        return
      }
      
      // Actualizar el item con los datos sugeridos
      const item = this.currentValidatingItem
      const suggested = this.suggestedProduct
      
      console.log('🔄 [STRATEGY] Aplicando sugerencia:', suggested)
      
      if (suggested.type === 'expiry_date') {
        // Actualizar fecha de vencimiento - MANTENER FORMATO COMPLETO CON HORA
        item.expiry_date = suggested.value // Mantener formato completo: 2027-01-30T00:00:00
        console.log(`📅 [STRATEGY] Fecha de vencimiento actualizada: ${item.expiry_date}`)
        
        // CRÍTICO: También actualizar el lot_number correspondiente a esta fecha de vencimiento
        console.log(`🔍 [STRATEGY] Buscando lote correspondiente a fecha ${suggested.value}`)
        await this.findCorrectLotForExpiryDate(item, suggested.value)
      } else if (suggested.type === 'lot') {
        // Actualizar lote
        const oldLotNumber = item.lot_number
        item.lot_number = suggested.value
        console.log(`🏷️ [STRATEGY] Lote actualizado: ${oldLotNumber} → ${item.lot_number}`)
        
        // CRÍTICO: También actualizar expiry_date correspondiente al nuevo lote
        console.log(`🔍 [STRATEGY] Buscando fecha de vencimiento correspondiente al lote ${suggested.value}`)
        await this.findCorrectExpiryDateForLot(item, suggested.value)
      }
      
      // Marcar como cumpliendo la estrategia
      item.strategy_compliant = true
      item.strategy_warning = null
      item.suggested_product = null
      
      this.closeStrategyWarningDialog()
      this.showSuccess('Producto actualizado para cumplir con la estrategia.')
      
      // Re-enfocar el input del escáner
      this.focusScannerInput()
    },
    
    // Función para encontrar el lot_number correcto basado en expiry_date
    async findCorrectLotForExpiryDate(item, targetExpiryDate) {
      try {
        console.log(`🔍 [LOT_FINDER] Buscando lote para expiry_date: ${targetExpiryDate} en producto ${item.product_id}`)
        
        // Hacer llamada al endpoint available-items para obtener información detallada
        const response = await api.get(`/inventory/products/${item.product_id}/available-items`)
        const availableData = response.data
        
        console.log(`📊 [LOT_FINDER] Available data items:`, availableData.items)
        
        // Usar la información detallada de items para mapeo exacto
        if (availableData.items) {
          console.log(`🔍 [LOT_FINDER] Searching in ${availableData.items.length} detailed items`)
          console.log(`🎯 [LOT_FINDER] Target expiry_date: "${targetExpiryDate}"`)
          
          // Normalizar la fecha objetivo para comparación
          const normalizeDate = (dateStr) => {
            if (!dateStr) return null
            
            // Convertir a Date object y luego a formato ISO para comparar solo la fecha
            const date = new Date(dateStr)
            if (isNaN(date.getTime())) return null
            
            // Retornar solo la parte de fecha en formato YYYY-MM-DD
            return date.toISOString().substring(0, 10)
          }
          
          const targetDateNormalized = normalizeDate(targetExpiryDate)
          console.log(`🎯 [LOT_FINDER] Target date normalized: "${targetDateNormalized}"`)
          
          // Buscar el item que tenga exactamente la fecha de vencimiento objetivo
          const matchingItem = availableData.items.find(detailItem => {
            if (!detailItem.expiry_date) return false
            
            const detailDateNormalized = normalizeDate(detailItem.expiry_date)
            
            console.log(`🔄 [LOT_FINDER] Comparing item:`)
            console.log(`   - lot_number: "${detailItem.lot_number}"`)
            console.log(`   - expiry_date: "${detailItem.expiry_date}"`)
            console.log(`   - normalized: "${detailDateNormalized}"`)
            console.log(`   - match? ${detailDateNormalized === targetDateNormalized}`)
            
            return detailDateNormalized === targetDateNormalized
          })
          
          if (matchingItem) {
            console.log(`✅ [LOT_FINDER] Encontrado mapeo exacto:`)
            console.log(`   - lot_number: "${matchingItem.lot_number}"`)
            console.log(`   - expiry_date: "${matchingItem.expiry_date}"`)
            
            // Actualizar tanto lote como fecha para asegurar consistencia
            const oldLotNumber = item.lot_number
            const oldExpiryDate = item.expiry_date
            
            item.lot_number = matchingItem.lot_number
            
            // Mantener formato ISO para consistency
            const normalizedTargetDate = new Date(targetExpiryDate).toISOString()
            item.expiry_date = normalizedTargetDate
            
            console.log(`🔄 [LOT_FINDER] CAMBIOS APLICADOS:`)
            console.log(`   - lot_number: "${oldLotNumber}" → "${matchingItem.lot_number}"`)
            console.log(`   - expiry_date: "${oldExpiryDate}" → "${normalizedTargetDate}"`)
          } else {
            console.log(`❌ [LOT_FINDER] No se encontró item con expiry_date: ${targetExpiryDate}`)
            console.log(`🔍 [LOT_FINDER] Available items:`)
            availableData.items.forEach((item, i) => {
              console.log(`   ${i + 1}. lot: "${item.lot_number}", expiry: "${item.expiry_date}", normalized: "${normalizeDate(item.expiry_date)}"`)
            })
          }
        } else {
          console.log(`❌ [LOT_FINDER] No hay información detallada de items en la respuesta`)
        }
        
      } catch (error) {
        console.error('❌ [LOT_FINDER] Error buscando lote correcto:', error)
      }
    },
    
    // Función para encontrar el expiry_date correcto basado en lot_number  
    async findCorrectExpiryDateForLot(item, targetLotNumber) {
      try {
        console.log(`🔍 [EXPIRY_FINDER] Buscando fecha de vencimiento para lote: ${targetLotNumber} en producto ${item.product_id}`)
        
        // Hacer llamada al endpoint available-items para obtener información detallada
        const response = await api.get(`/inventory/products/${item.product_id}/available-items`)
        const availableData = response.data
        
        console.log(`📊 [EXPIRY_FINDER] Available data items:`, availableData.items)
        
        // Usar la información detallada de items para mapeo exacto
        if (availableData.items) {
          console.log(`🔍 [EXPIRY_FINDER] Searching in ${availableData.items.length} detailed items`)
          console.log(`🎯 [EXPIRY_FINDER] Target lot_number: "${targetLotNumber}"`)
          
          // Buscar el item que tenga exactamente el lote objetivo
          const matchingItem = availableData.items.find(detailItem => {
            return detailItem.lot_number === targetLotNumber
          })
          
          if (matchingItem) {
            console.log(`✅ [EXPIRY_FINDER] Encontrado mapeo exacto:`)
            console.log(`   - lot_number: "${matchingItem.lot_number}"`)
            console.log(`   - expiry_date: "${matchingItem.expiry_date}"`)
            
            // Actualizar fecha de vencimiento para consistencia
            const oldExpiryDate = item.expiry_date
            
            // Mantener formato ISO para consistency
            if (matchingItem.expiry_date) {
              const normalizedExpiryDate = new Date(matchingItem.expiry_date).toISOString()
              item.expiry_date = normalizedExpiryDate
            } else {
              item.expiry_date = null
            }
            
            console.log(`🔄 [EXPIRY_FINDER] CAMBIOS APLICADOS:`)
            console.log(`   - expiry_date: "${oldExpiryDate}" → "${item.expiry_date}"`)
          } else {
            console.log(`❌ [EXPIRY_FINDER] No se encontró item con lot_number: ${targetLotNumber}`)
            console.log(`🔍 [EXPIRY_FINDER] Available items:`)
            availableData.items.forEach((item, i) => {
              console.log(`   ${i + 1}. lot: "${item.lot_number}", expiry: "${item.expiry_date}"`)
            })
          }
        } else {
          console.log(`❌ [EXPIRY_FINDER] No hay información detallada de items en la respuesta`)
        }
        
      } catch (error) {
        console.error('❌ [EXPIRY_FINDER] Error buscando fecha de vencimiento correcta:', error)
      }
    },
    
    // CRUD operations
    async saveEgreso() {
      if (!this.canSaveEgreso) return
      
      try {
        this.submitting = true
        
        const data = {
          area_servicio: this.newEgreso.area_servicio,
          strategy: this.newEgreso.strategy,
          notes: this.newEgreso.notes,
          items: this.newEgreso.items
        }
        
        console.log('📤 [SAVE EGRESO] Sending data to backend:')
        console.log('🏥 Área/Servicio:', data.area_servicio)
        console.log('📋 Strategy:', data.strategy)
        console.log('📝 Notes:', data.notes)
        console.log('📦 Items:', data.items)
        data.items.forEach((item, index) => {
          console.log(`   Item ${index + 1}:`, {
            product_id: item.product_id,
            sku: item.sku,
            quantity: item.quantity,
            lot_number: item.lot_number,
            expiry_date: item.expiry_date,
            strategy_compliant: item.strategy_compliant
          })
        })
        
        const response = await api.post('/egresos/egress-orders', data)
        
        if (response.data) {
          this.showSuccess('Orden de egreso creada exitosamente')
          this.closeCreateModal()
          await this.loadEgresos()
        }
        
      } catch (error) {
        console.error('Error saving egreso:', error)
        const errorMessage = error.response?.data?.error || 'Error al crear orden de egreso'
        this.showError(errorMessage)
      } finally {
        this.submitting = false
      }
    },
    
    viewEgreso(egreso) {
      // TODO: Implementar modal de visualización
      console.log('View egreso:', egreso)
      this.showInfo('Vista de detalle pendiente de implementar')
    },
    
    viewEgreso(egreso) {
      this.selectedEgreso = egreso
      this.showReportModal = true
    },
    
    printReport() {
      // Crear una ventana nueva para imprimir
      const printWindow = window.open('', '_blank')
      const reportContent = this.generatePrintableReport()
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Reporte de Salida - ${this.selectedEgreso.order_number}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .info-section { margin-bottom: 20px; }
            .info-section h3 { margin-bottom: 10px; border-bottom: 2px solid #333; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .summary { margin-top: 20px; }
            .summary-item { display: flex; justify-content: space-between; margin-bottom: 5px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${reportContent}
        </body>
        </html>
      `)
      
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
      printWindow.close()
    },
    
    generatePrintableReport() {
      if (!this.selectedEgreso) return ''
      
      const items = this.selectedEgreso.items || []
      const totalSKUs = items.length
      const totalUnits = items.reduce((sum, item) => sum + (item.quantity || 0), 0)
      
      return `
        <div class="header">
          <h1>REPORTE DE SALIDA</h1>
          <h2>Orden: ${this.selectedEgreso.order_number}</h2>
        </div>
        
        <div class="info-section">
          <h3>Información General</h3>
          <p><strong>Fecha:</strong> ${this.formatDate(this.selectedEgreso.created_at)}</p>
          <p><strong>Área/Servicio:</strong> ${this.getAreaServicioText(this.selectedEgreso.area_servicio) || 'No especificado'}</p>
          <p><strong>Estrategia:</strong> ${this.getStrategyText(this.selectedEgreso.strategy)}</p>
          <p><strong>Usuario:</strong> ${this.selectedEgreso.user || 'Sistema'}</p>
          <p><strong>Notas:</strong> ${this.selectedEgreso.notes || 'Sin notas'}</p>
        </div>
        
        <div class="info-section">
          <h3>Productos</h3>
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>SKU</th>
                <th>Cantidad</th>
                <th>Lote</th>
                <th>Vencimiento</th>
                <th>S/N</th>
                <th>F. Fabricación</th>
                <th>F. Empaque</th>
                <th>SSCC</th>
                <th>GLN</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => `
                <tr>
                  <td>${item.product?.name || 'Producto desconocido'}</td>
                  <td>${item.product?.sku || '-'}</td>
                  <td>${item.quantity}</td>
                  <td>${item.lot_number || '-'}</td>
                  <td>${this.formatDate(item.expiry_date) || '-'}</td>
                  <td>${item.serial_number || '-'}</td>
                  <td>${this.formatDate(item.production_date) || '-'}</td>
                  <td>${this.formatDate(item.pack_date) || '-'}</td>
                  <td>${item.sscc || '-'}</td>
                  <td>${item.gln || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="summary">
          <h3>Resumen</h3>
          <div class="summary-item">
            <strong>Total de SKUs:</strong>
            <span>${totalSKUs}</span>
          </div>
          <div class="summary-item">
            <strong>Total de Unidades:</strong>
            <span>${totalUnits}</span>
          </div>
        </div>
      `
    },
    
    async deleteEgreso(egreso) {
      if (!confirm(`¿Está seguro de eliminar la orden ${egreso.order_number}?`)) {
        return
      }
      
      try {
        await api.delete(`/egresos/egress-orders/${egreso.id}`)
        this.showSuccess('Orden eliminada exitosamente')
        await this.loadEgresos()
      } catch (error) {
        console.error('Error deleting egreso:', error)
        this.showError('Error al eliminar orden')
      }
    },
    
    // Utility methods
    formatDate(dateString) {
      if (!dateString) return '-'
      try {
        return new Date(dateString).toLocaleDateString('es-ES')
      } catch {
        return dateString
      }
    },
    
    getStrategyColor(strategy) {
      const colors = {
        'libre': 'grey',
        'fifo': 'blue',
        'fefo': 'orange',
        'lifo': 'green'
      }
      return colors[strategy] || 'grey'
    },
    
    getStrategyText(strategy) {
      const texts = {
        'libre': 'Libre',
        'fifo': 'FIFO',
        'fefo': 'FEFO',
        'lifo': 'LIFO'
      }
      return texts[strategy] || strategy
    },
    
    getAreaServicioText(value) {
      const area = this.areasServicios.find(a => a.value === value)
      return area ? area.title : value
    },

    // ========== UTILIDADES PARA NUEVOS CAMPOS GS1 ==========
    
    hasWeightOrCustomData(item) {
      return !!(item.gross_weight_kg || item.gross_weight_lb || 
               item.net_weight_kg || item.net_weight_lb ||
               item.gdti || this.hasCustomFields(item))
    },
    
    hasCustomFields(item) {
      const customFields = ['custom_field_90', 'custom_field_91', 'custom_field_92', 
                           'custom_field_93', 'custom_field_94', 'custom_field_95',
                           'custom_field_96', 'custom_field_97', 'custom_field_98', 'custom_field_99']
      return customFields.some(field => item[field])
    },
    
    getCustomFieldsCount(item) {
      const customFields = ['custom_field_90', 'custom_field_91', 'custom_field_92', 
                           'custom_field_93', 'custom_field_94', 'custom_field_95',
                           'custom_field_96', 'custom_field_97', 'custom_field_98', 'custom_field_99']
      return customFields.filter(field => item[field]).length
    },

    // ========== FUNCIONES INFORMATIVAS PARA CÓDIGOS NO COMERCIALES ==========
    
    showCodeInformation(analysis, productData) {
      const codeType = this.identifyCodeType(analysis, productData)
      const details = this.formatCodeDetails(productData)
      
      const message = `📊 Código ${codeType} detectado:\n${details}\n\n⚡ Listo para siguiente escaneo`
      
      // Mostrar mensaje informativo por 5 segundos
      this.showInfoMessage(message, 5000)
      
      console.log(`📊 [INFO] Código no comercial parseado:`, {
        type: codeType,
        gtin: analysis.gtin,
        data: productData
      })
    },
    
    showBasicCodeInformation(analysis) {
      const codeType = this.identifyCodeTypeByGTIN(analysis.gtin)
      const message = `📦 Código ${codeType} detectado:\n🏷️ GTIN: ${analysis.gtin}\n\n⚡ Listo para siguiente escaneo`
      
      // Mostrar mensaje informativo por 5 segundos
      this.showInfoMessage(message, 5000)
      
      console.log(`📦 [INFO] Código GS1 básico:`, {
        type: codeType,
        gtin: analysis.gtin
      })
    },
    
    identifyCodeType(analysis, productData) {
      // Identificar tipo de código basado en los AIs presentes
      if (productData.sscc) return 'CONTENEDOR (SSCC)'
      if (productData.gsin) return 'ENVÍO (GSIN)'  
      if (productData.gdti) return 'DOCUMENTO (GDTI)'
      if (productData.gln) return 'UBICACIÓN (GLN)'
      if (productData.gross_weight_kg || productData.net_weight_kg) return 'PRODUCTO CON PESO'
      if (productData.custom_field_90 || productData.custom_field_91) return 'CÓDIGO PERSONALIZADO'
      if (analysis.gtin) return 'PRODUCTO GS1'
      
      return 'GS1'
    },
    
    identifyCodeTypeByGTIN(gtin) {
      if (!gtin) return 'GS1'
      
      // Análisis básico del GTIN para identificar tipo
      if (gtin.startsWith('00')) return 'CONTENEDOR/PALLET'
      if (gtin.startsWith('02')) return 'PRODUCTO VARIABLE'
      if (gtin.startsWith('1') || gtin.startsWith('2')) return 'PRODUCTO COMERCIAL'
      
      return 'PRODUCTO GS1'
    },
    
    formatCodeDetails(productData) {
      const details = []
      
      // Información principal
      if (productData.sscc) details.push(`📦 SSCC: ${productData.sscc}`)
      if (productData.gsin) details.push(`🚛 GSIN: ${productData.gsin}`)
      if (productData.gdti) details.push(`📄 GDTI: ${productData.gdti}`)
      if (productData.gln) details.push(`🏢 GLN: ${productData.gln}`)
      
      // Información de producto
      if (productData.lot_number) details.push(`🏷️ Lote: ${productData.lot_number}`)
      if (productData.serial_number) details.push(`🔢 Serie: ${productData.serial_number}`)
      if (productData.expiry_date) details.push(`📅 Vence: ${this.formatDate(productData.expiry_date)}`)
      if (productData.production_date) details.push(`🏭 Prod.: ${this.formatDate(productData.production_date)}`)
      
      // Pesos
      if (productData.gross_weight_kg) details.push(`⚖️ Peso bruto: ${productData.gross_weight_kg}kg`)
      if (productData.net_weight_kg) details.push(`⚖️ Peso neto: ${productData.net_weight_kg}kg`)
      if (productData.gross_weight_lb) details.push(`⚖️ Peso bruto: ${productData.gross_weight_lb}lb`)
      if (productData.net_weight_lb) details.push(`⚖️ Peso neto: ${productData.net_weight_lb}lb`)
      
      // Campos personalizados
      const customFields = ['custom_field_90', 'custom_field_91', 'custom_field_92', 
                           'custom_field_93', 'custom_field_94', 'custom_field_95',
                           'custom_field_96', 'custom_field_97', 'custom_field_98', 'custom_field_99']
      
      customFields.forEach((field, index) => {
        if (productData[field]) {
          details.push(`🔧 Campo ${90 + index}: ${productData[field]}`)
        }
      })
      
      return details.length > 0 ? details.join('\n') : '📋 Información GS1 detectada'
    },
    
    showInfoMessage(message, duration = 5000) {
      // Usar el sistema de snackbar existente
      this.snackbar = {
        show: true,
        message: message,
        color: 'info',
        timeout: duration
      }
    },
    

    
    // Notificaciones
    showSuccess(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'success',
        timeout: 4000
      }
    },
    
    showError(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'error',
        timeout: 6000
      }
    },
    
    showInfo(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'info',
        timeout: 4000
      }
    },
    
    // Métodos de validación de estrategias
    validateFIFOStrategy(item, availableItems) {
      console.log(`🔍 [FIFO] Validating item:`, item)
      console.log(`📦 [FIFO] Available items:`, availableItems)
      
      if (!item.lot_number) {
        console.log(`❌ [FIFO] No lot_number on item`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const lots = availableItems.lots || []
      if (lots.length === 0) {
        console.log(`❌ [FIFO] No lots available`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`🏷️ [FIFO] Available lots:`, lots.map(l => `${l.value} (${l.entry_date})`))
      
      // Ordenar por fecha de entrada (más antigua primero)
      const sortedLots = [...lots].sort((a, b) => new Date(a.entry_date) - new Date(b.entry_date))
      console.log(`🗂️ [FIFO] Sorted lots (oldest first):`, sortedLots.map(l => `${l.value} (${l.entry_date})`))
      
      // Encontrar el lote escaneado
      const scannedLot = lots.find(lot => lot.value === item.lot_number)
      if (!scannedLot) {
        console.log(`❌ [FIFO] Scanned lot '${item.lot_number}' not found in available lots`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`📦 [FIFO] Scanned lot found:`, scannedLot)
      
      // Verificar si hay lotes más antiguos con stock disponible
      const olderLotsWithStock = sortedLots.filter(lot => 
        new Date(lot.entry_date) < new Date(scannedLot.entry_date) && 
        lot.quantity > 0
      )
      
      console.log(`⏰ [FIFO] Older lots with stock:`, olderLotsWithStock.map(l => `${l.value} (${l.entry_date}, qty: ${l.quantity})`))
      
      if (olderLotsWithStock.length > 0) {
        const oldestLot = olderLotsWithStock[0]
        console.log(`❌ [FIFO] Strategy violation! Suggesting oldest lot:`, oldestLot)
        return {
          compliant: false,
          warning: `FIFO: Hay ${olderLotsWithStock.length} lote(s) con fecha de ingreso más antigua disponible(s). El más antiguo es '${oldestLot.value}' con ${oldestLot.quantity} unidades (ingreso: ${this.formatDate(oldestLot.entry_date)})`,
          suggestedItem: {
            type: 'lot',
            value: oldestLot.value,
            date: oldestLot.entry_date,
            available_quantity: oldestLot.quantity
          }
        }
      }
      
      console.log(`✅ [FIFO] Strategy compliant`)
      return { compliant: true, warning: null, suggestedItem: null }
    },
    
    validateLIFOStrategy(item, availableItems) {
      console.log(`🔍 [LIFO] Validating item:`, item)
      console.log(`📦 [LIFO] Available items:`, availableItems)
      
      if (!item.lot_number) {
        console.log(`❌ [LIFO] No lot_number on item`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const lots = availableItems.lots || []
      if (lots.length === 0) {
        console.log(`❌ [LIFO] No lots available`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`🏷️ [LIFO] Available lots:`, lots.map(l => `${l.value} (${l.entry_date})`))
      
      // Ordenar por fecha de entrada (más reciente primero)
      const sortedLots = [...lots].sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date))
      console.log(`🗂️ [LIFO] Sorted lots (newest first):`, sortedLots.map(l => `${l.value} (${l.entry_date})`))
      
      // Encontrar el lote escaneado
      const scannedLot = lots.find(lot => lot.value === item.lot_number)
      if (!scannedLot) {
        console.log(`❌ [LIFO] Scanned lot '${item.lot_number}' not found in available lots`)
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      console.log(`📦 [LIFO] Scanned lot found:`, scannedLot)
      
      // Verificar si hay lotes más recientes con stock disponible
      const newerLotsWithStock = sortedLots.filter(lot => 
        new Date(lot.entry_date) > new Date(scannedLot.entry_date) && 
        lot.quantity > 0
      )
      
      console.log(`⏰ [LIFO] Newer lots with stock:`, newerLotsWithStock.map(l => `${l.value} (${l.entry_date}, qty: ${l.quantity})`))
      
      if (newerLotsWithStock.length > 0) {
        const newestLot = newerLotsWithStock[0]
        console.log(`❌ [LIFO] Strategy violation! Suggesting newest lot:`, newestLot)
        return {
          compliant: false,
          warning: `LIFO: Hay ${newerLotsWithStock.length} lote(s) con fecha de ingreso más reciente disponible(s). El más reciente es '${newestLot.value}' con ${newestLot.quantity} unidades (ingreso: ${this.formatDate(newestLot.entry_date)})`,
          suggestedItem: {
            type: 'lot',
            value: newestLot.value,
            date: newestLot.entry_date,
            available_quantity: newestLot.quantity
          }
        }
      }
      
      console.log(`✅ [LIFO] Strategy compliant`)
      return { compliant: true, warning: null, suggestedItem: null }
    },
    
    validateFEFOStrategy(item, availableItems) {
      if (!item.expiry_date) {
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const expiryDates = availableItems.expiry_dates || []
      if (expiryDates.length === 0) {
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      // Ordenar por fecha de vencimiento (más próxima primero)
      const sortedExpiry = [...expiryDates].sort((a, b) => new Date(a.expiry_date || a.value) - new Date(b.expiry_date || b.value))
      
      // Normalizar fecha del item escaneado para comparación (solo fecha, sin hora)
      const scannedDateStr = item.expiry_date
      const scannedDateOnly = scannedDateStr ? scannedDateStr.substring(0, 10) : ''
      
      // Encontrar la fecha de vencimiento escaneada (comparando solo la parte de fecha)
      const scannedExpiry = expiryDates.find(exp => {
        const expDateStr = exp.expiry_date || exp.value || ''
        const expDateOnly = expDateStr.substring(0, 10)
        return expDateOnly === scannedDateOnly
      })
      
      if (!scannedExpiry) {
        return { compliant: true, warning: null, suggestedItem: null }
      }
      
      const scannedDate = new Date(scannedExpiry.expiry_date || scannedExpiry.value)
      
      // Verificar si hay fechas de vencimiento más próximas con stock disponible
      const earlierExpiryWithStock = sortedExpiry.filter(exp => {
        const expDate = new Date(exp.expiry_date || exp.value)
        const hasStock = exp.quantity > 0
        const isEarlier = expDate < scannedDate
        return isEarlier && hasStock
      })
      
      if (earlierExpiryWithStock.length > 0) {
        const earliestExpiry = earlierExpiryWithStock[0]
        return {
          compliant: false,
          warning: `FEFO: Hay ${earlierExpiryWithStock.length} fecha(s) de vencimiento más próxima(s) disponible(s). La más próxima vence el ${this.formatDate(earliestExpiry.expiry_date || earliestExpiry.value)} con ${earliestExpiry.quantity} unidades`,
          suggestedItem: {
            type: 'expiry_date',
            value: earliestExpiry.expiry_date || earliestExpiry.value,
            available_quantity: earliestExpiry.quantity
          }
        }
      }
      
      return { compliant: true, warning: null, suggestedItem: null }
    }
  }
}
</script>

<style scoped>
.egresos-container {
  padding: 20px;
}

.egresos-title {
  font-size: 1.5rem;
  font-weight: 500;
}

.egresos-filters .v-col {
  padding: 4px 8px;
}

.egresos-table {
  margin-top: 16px;
}

/* Estilos para la tabla de items */
.egresos-items-table {
  width: 100%;
  table-layout: fixed;
}

.egresos-items-table th,
.egresos-items-table td {
  text-align: left;
  vertical-align: middle;
  padding: 8px 12px !important;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.egresos-items-table .product-col {
  width: 25%;
  min-width: 200px;
}

.egresos-items-table .sku-col {
  width: 12%;
  min-width: 100px;
}

.egresos-items-table .quantity-col {
  width: 8%;
  min-width: 80px;
  text-align: center;
}

.egresos-items-table .lot-col {
  width: 12%;
  min-width: 100px;
}

.egresos-items-table .expiry-col {
  width: 10%;
  min-width: 100px;
}

.egresos-items-table .serial-col {
  width: 10%;
  min-width: 100px;
}

.egresos-items-table .production-col {
  width: 8%;
  min-width: 90px;
}

.egresos-items-table .pack-col {
  width: 8%;
  min-width: 90px;
}

.egresos-items-table .sscc-col {
  width: 8%;
  min-width: 100px;
  font-size: 0.8em;
}

.egresos-items-table .gln-col {
  width: 7%;
  min-width: 80px;
  font-size: 0.8em;
}

.egresos-items-table .strategy-col {
  width: 8%;
  min-width: 90px;
  text-align: center;
}

.egresos-items-table .actions-col {
  width: 5%;
  min-width: 60px;
  text-align: center;
}

/* Estilos para el modal de reporte */
.report-content {
  max-height: 70vh;
  overflow-y: auto;
}

.report-header h3 {
  color: #1976d2;
  margin-bottom: 15px;
}

.report-table {
  font-size: 0.9em;
}

.report-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.report-summary {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.report-summary > div {
  padding: 5px 0;
}

/* Responsividad */
@media (max-width: 768px) {
  .egresos-container {
    padding: 10px;
  }
  
  .egresos-filters .v-col {
    padding: 2px 4px;
  }
}
</style> 